
"use client"

import { useEffect } from "react"
import { useTheme } from "next-themes"
import { Toaster as Sonner, toast, useSonner } from "sonner"

type ToasterProps = React.ComponentProps<typeof Sonner>

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()
  const { toasts } = useSonner()

  // A click anywhere else on the screen dismisses whatever is showing. Sonner
  // pauses its own auto-dismiss timer while the pointer is over the toast area
  // (and while the window is unfocused), so a toast that lands under the cursor
  // could otherwise sit there until it was closed by hand.
  //
  // Clicks INSIDE a toast are deliberately left alone: its close/action buttons
  // keep working and the message stays selectable for copying.
  // Mirrors hrpayroll/src/components/ui/sonner.tsx.
  const hasToasts = toasts.length > 0
  useEffect(() => {
    if (!hasToasts) return
    const dismissOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Element | null
      if (typeof target?.closest !== "function") return
      if (target.closest("[data-sonner-toast]")) return
      toast.dismiss()
    }
    // pointerdown (capture) so the toast clears on press, before the click that
    // may unmount whatever was clicked.
    document.addEventListener("pointerdown", dismissOnOutsideClick, true)
    return () =>
      document.removeEventListener("pointerdown", dismissOnOutsideClick, true)
  }, [hasToasts])

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      toastOptions={{
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }

